<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container justify-content-end">
        <span>
          <el-button
            :disabled="isSaving"
            class="cancel-button"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >{{ $t('button.cancel') }}</el-button>
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            class="save-button"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >{{ $t('button.save') }}</el-button>
        </span>
      </div>
    </template>

    <el-form
      v-loading="isLoading"
      ref="form"
      :disabled="isSaving"
      :model="form"
      :rules="formRules"
      class="round-border-form"
      label-position="top"
      label-width="200"
    >
      <h4 class="title">{{ $t(`featured.${form.type}`) }}</h4>

      <!-- Platform name -->
      <div class="form-group">
        <el-form-item :label="`${$t('featured.platform')}:`">
          <span>{{ $t(`featured.${form.platform}`) }}</span>
        </el-form-item>
      </div>
      <!-- Platform name -->

      <!-- Reference IDs for All Countries -->
      <h4 class="title">{{ $t('featured.referenceIds') }}</h4>
      <div
        v-if="form.countries"
        class="form-group pb-2"
      >
        <el-form-item
          v-for="code in countryList"
          :key="code"
          :rules="formRules.reference_ids"
          :prop="`countries.${code}.reference_ids`"
          class="py-2"
        >
          <div class="d-flex align-items-center">
            <span class="text-uppercase country-code">{{ code }}</span>
            <el-input
              :value="form.countries[code].isFollowHk ? form.countries.HK.reference_ids : form.countries[code].reference_ids"
              :disabled="form.countries[code].isFollowHk"
              class="mr-3"
              clearable
              @input="value => onReferenceIdsInput(code, value)"
            />
            <el-checkbox
              v-model="form.countries[code].isFollowHk"
              :class="{ hidden: code === 'HK' }"
            >{{ $t('featured.followHk') }}</el-checkbox>
          </div>
        </el-form-item>
      </div>
      <!-- Reference IDs for All Countries -->

    </el-form>
  </sticky-header-layout>
</template>

<script>
import { getFeaturedItemDetail, updateFeaturedItem } from '../../api/featured'
import { FormRules } from '../../utils/form'

const countryList = ['HK', 'TH', 'AU', 'ID', 'MY', 'PH', 'SG', 'UK', 'US', 'CA', 'TW']

export default {
  name: 'FeaturedDetail',
  props: {
    type: {
      type: String,
      default: undefined,
    },
    platform: {
      type: String,
      default: undefined,
    },
  },
  data() {
    const validateReferenceIds = (rule, value, callback) => {
      switch (this.form.type) {
        case 'magazines': {
          const count = (value.match(/,/g) || []).length;
          if (count > 3) { // only allow 4 ids
            callback(new Error(this.$t('error.youCanOnlyInput4Ids')))
          }
          break
        }
        default:
          break;
      }

      callback();
    };

    return {
      form: {
        id: '',
        type: '',
        country: '',
        platform: '',
        reference_ids: '',
      },
      formRules: {
        reference_ids: [
          FormRules.idList,
          { validator: validateReferenceIds, trigger: 'blur' },
          FormRules.maxNumOfId,
        ],
      },
      isEdited: false,
      isLoading: false,
      isSaving: false,
      countryList,
    }
  },
  watch: {
    form: {
      handler() {
        this.isEdited = true
      },
      deep: true,
    },
  },
  mounted() {
    this.getFeatured(this.type, this.platform)
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      return next(false)
    }

    if (!this.isEdited) {
      return next()
    }

    this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
      confirmButtonText: this.$t('button.leave'),
      cancelButtonText: this.$t('button.cancel'),
      type: 'warning',
    }).then(() => next()).catch(() => next(false))

    return next(false)
  },
  methods: {
    async editfeatured(form, type, platform) {
      this.isSaving = true
      try {
        await updateFeaturedItem(form, type, platform)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async getFeatured(type, platform) {
      this.isLoading = true
      try {
        const response = await getFeaturedItemDetail(type, platform)
        this.form = {
          ...response.data,
          countries: this.reduceCountryReferenceIds(response.data.countries),
        }
        this.$nextTick(() => {
          this.isEdited = false
          this.isLoading = false
        })
      } catch (error) {
        this.isLoading = false
      }
    },
    handleSaveSuccess() {
      this.isSaving = false
      this.isEdited = false
      this.goToFeaturedList()
    },
    handleSaveFail(error) {
      this.isSaving = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: this.$t('button.ok'),
      })
    },
    goToFeaturedList() {
      this.$router.push({ path: '/trending/featured-items' })
    },
    reshapeFormDataToServerFormat(form) {
      const newForm = { ...form }
      newForm.countries = Object.keys(form.countries).reduce((acc, countryCode) => {
        const { isFollowHk } = form.countries[countryCode]
        acc[countryCode] = isFollowHk ? null : form.countries[countryCode].reference_ids
        return acc
      }, {})
      return newForm
    },
    handleCancel() {
      this.goToFeaturedList()
    },
    handleSave() {
      this.$refs.form.validate().then(() => {
        const serverForm = this.reshapeFormDataToServerFormat(this.form)
        this.editfeatured(serverForm, this.type, this.platform)
      }).catch((error) => {
        console.error(error);
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
          confirmButtonText: this.$t('button.ok'),
        })
      })
    },
    onReferenceIdsInput(code, value) {
      this.form.countries[code].reference_ids = value.trim()
    },
    reduceCountryReferenceIds(data) {
      return countryList.reduce((acc, code) => {
        const isFollowHk = !data[code] && data[code] !== ''
        Object.assign(acc, {
          [code]: {
            reference_ids: isFollowHk ? data.HK : data[code],
            isFollowHk,
          },
        })
        return acc
      }, {})
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";
@import "~@/assets/styles/form.scss";

.hidden {
  visibility: hidden;
}

.country-code {
  width: 32px;
}

::v-deep {
  .el-form-item__error {
    padding-left: 28px;
  }
}
</style>

<style lang="scss">
.el-form--label-top .el-form-item__label {
  padding: 0;
}
</style>
